import { createI18n } from 'vue-i18n'
import de from '@/i18n/locales/de.json'
import en from '@/i18n/locales/en.json'
import { en as enVuetify, de as deVuetify } from 'vuetify/locale'

export const languages = {
  GERMAN: 'de',
  ENGLISH: 'en'
} as const
export type languages = (typeof languages)[keyof typeof languages]

export enum NumberFormat {
  DECIMAL = 'decimal',
  INTEGER = 'integer'
}

const dateOfBirth: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: 'utc'
}

const consultationLogDate: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
}

const consultationLogDateTime: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  minute: '2-digit',
  hour: '2-digit'
}

const fileUploadedAtDateTime: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  minute: '2-digit',
  hour: '2-digit'
}

const todayDateTime: Intl.DateTimeFormatOptions = {
  minute: '2-digit',
  hour: '2-digit'
}

const beforeTodayDateTime: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  minute: '2-digit',
  hour: '2-digit'
}

const fullDate: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'long',
  year: 'numeric'
}

const timeDE: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: false }
const timeEN: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true }

const dateDE: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' }
const dateEN: Intl.DateTimeFormatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' }

const dateTimeDE: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
}
const dateTimeEN: Intl.DateTimeFormatOptions = {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true
}

const dateTimeShortDE: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
}
const dateTimeShortEN: Intl.DateTimeFormatOptions = {
  month: '2-digit',
  day: '2-digit',
  year: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true
}

const dateShortDE: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit'
}
const dateShortEN: Intl.DateTimeFormatOptions = {
  month: '2-digit',
  day: '2-digit',
  year: '2-digit'
}

const dateTimeWithMonthDE: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
}
const dateTimeWithMonthEN: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true
}

const numberFormats = {
  [languages.GERMAN]: {
    [NumberFormat.DECIMAL]: {
      style: 'decimal' as const,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    },
    [NumberFormat.INTEGER]: {
      style: 'decimal' as const,
      maximumFractionDigits: 0
    }
  },
  [languages.ENGLISH]: {
    [NumberFormat.DECIMAL]: {
      style: 'decimal' as const,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    },
    [NumberFormat.INTEGER]: {
      style: 'decimal' as const,
      maximumFractionDigits: 0
    }
  }
}

const missingTranslations = new Set<string>()
let debounceTimeout: ReturnType<typeof setTimeout> | null = null

const i18n = createI18n({
  legacy: false,
  locale: languages.ENGLISH,
  fallbackLocale: languages.ENGLISH,
  messages: {
    de: { $vuetify: { ...deVuetify }, ...de },
    en: { $vuetify: { ...enVuetify }, ...en }
  },
  datetimeFormats: {
    [languages.GERMAN]: {
      dateOfBirth,
      consultationLogDate,
      consultationLogDateTime,
      fileUploadedAtDateTime,
      beforeTodayDateTime,
      todayDateTime,
      fullDate,
      time: timeDE,
      date: dateDE,
      dateTime: dateTimeDE,
      dateTimeShort: dateTimeShortDE,
      dateShort: dateShortDE,
      dateTimeWithMonth: dateTimeWithMonthDE
    },
    [languages.ENGLISH]: {
      dateOfBirth,
      consultationLogDate,
      consultationLogDateTime,
      fileUploadedAtDateTime,
      beforeTodayDateTime,
      todayDateTime,
      fullDate,
      time: timeEN,
      date: dateEN,
      dateTime: dateTimeEN,
      dateTimeShort: dateTimeShortEN,
      dateShort: dateShortEN,
      dateTimeWithMonth: dateTimeWithMonthEN
    }
  },
  numberFormats,
  missing: (locale: string, key: string) => {
    missingTranslations.add(key)

    if (import.meta.env.DEV) {
      console.warn(`Not found '${key}' key in '${locale}' locale messages.`)

      if (debounceTimeout) {
        clearTimeout(debounceTimeout)
      }

      debounceTimeout = setTimeout(() => {
        const translationsObject = Array.from(missingTranslations)
          .sort()
          .reduce(
            (acc, k) => {
              acc[k] = k
              return acc
            },
            {} as Record<string, string>
          )

        console.warn('Missing translations as JSON:', JSON.stringify(translationsObject, null, 2))
      }, 1000)
    }
  }
})

export function changeLanguage(language: languages) {
  i18n.global.locale.value = language
}

export function validateLanguageCode(languageCode: string): void {
  if (!(Object.values(languages) as string[]).includes(languageCode)) {
    throw new Error(`invalid language code ${languageCode}`)
  }
}

export function formatDateWithDots(
  date: Date | string,
  locale: string = i18n.global.locale.value
): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const day = d.getDate().toString().padStart(2, '0')
  let month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(d)
  if (!month.endsWith('.')) month += '.'
  const year = d.getFullYear()
  return `${day}. ${month} ${year}`
}

export default i18n
